import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall/index.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandL } from '@sorare/core/src/atoms/typography/index.tsx';
import { Avatar } from '@sorare/core/src/components/user/Avatar/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { isType } from '@sorare/core/src/lib/gql/index.ts';
import { SetColorTheme } from '@sorare/core/src/set/SetColorTheme/index.tsx';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';

import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import {
  ButtonsWrapper,
  Root,
} from '#src/components/missions/stories/RewardUnlockedStory/styles.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import type { RefereeExclusiveReward_currentUser } from './__generated__/index.graphql.ts';

const LogoWrapper = styled.div`
  position: absolute;
  top: calc(6 * var(--unit));
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const AffiliatePicture = styled.img`
  width: calc(10 * var(--unit));
  height: calc(10 * var(--unit));
  border-radius: 50%;
  object-fit: cover;
`;

const Content = styled(Vertical).attrs({ center: true, gap: 3 })`
  h1 {
    text-wrap: balance;
  }
`;

const Name = styled.span`
  color: var(--c-set-surface-secondary);
`;

type Props = {
  onClose: () => void;
  currentUser: Nullable<RefereeExclusiveReward_currentUser>;
};

export const RefereeExclusiveReward = ({ currentUser, onClose }: Props) => {
  const { sport } = useSportContext();
  const { visual, background } = useCommonGameSet();
  const [claimTask, { loading: claiming }] = useClaimTask();
  const [revealed, setRevealed] = useState(false);

  const { inviteTaskAsReferee, referralAsReferee } = currentUser || {};

  if (!inviteTaskAsReferee) {
    return null;
  }

  const onReveal = async () => {
    if (
      inviteTaskAsReferee.aasmState === ManagerProgressionTaskState.COMPLETED
    ) {
      await claimTask({
        variables: { taskId: inviteTaskAsReferee.id, sport },
      });
    }
    setRevealed(true);
  };

  if (revealed) {
    return (
      <RewardUnlockedStory
        onClose={onClose}
        rewardConfigs={inviteTaskAsReferee.rewardConfigs}
        useSetBackground
        hideAction
      />
    );
  }

  const referrer = referralAsReferee?.referrer;
  const isUserReferrer = isType(referrer, 'User');
  const name =
    (isUserReferrer ? referrer.nickname : referrer?.displayName) || 'Sorare';
  const affiliatePictureUrl =
    !isUserReferrer && referrer?.pictureUrl ? referrer.pictureUrl : null;
  const { SetLogoImg } = visual.brand;

  return (
    <SetColorTheme>
      <Root
        className="hideBackground"
        style={visual.welcomeBackDialog || background}
      >
        {!!SetLogoImg && (
          <LogoWrapper>
            <SetLogoImg height={32} color="var(--c-white)" />
          </LogoWrapper>
        )}
        <Content>
          {isUserReferrer ? (
            <Avatar user={referrer} size={10} rounded />
          ) : affiliatePictureUrl ? (
            <AffiliatePicture src={affiliatePictureUrl} alt="" />
          ) : (
            <StarBall width={64} height={64} />
          )}
          <BrandL as="h1" className="text-center" color="var(--c-white)">
            <FormattedMessage
              defaultMessage="<green>{name}</green> has an exclusive reward for you…"
              values={{
                name,
                green: chunks => <Name>{chunks}</Name>,
              }}
            />
          </BrandL>
        </Content>
        <ButtonsWrapper className="dark-mode">
          <Button
            size="large"
            color="primary"
            onClick={onReveal}
            disabled={claiming}
          >
            <FormattedMessage defaultMessage="Reveal my rewards" />
          </Button>
        </ButtonsWrapper>
      </Root>
    </SetColorTheme>
  );
};

RefereeExclusiveReward.fragments = {
  currentUser: gql`
    fragment RefereeExclusiveReward_currentUser on CurrentUser {
      slug
      inviteTaskAsReferee(sport: $sport) {
        id
        aasmState
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
      referralAsReferee(sport: $sport) {
        id
        referrer {
          ... on User {
            slug
            nickname
            ...Avatar_user
          }
          ... on UserSource {
            id
            name
            displayName
            pictureUrl
          }
        }
      }
    }
    ${Avatar.fragments.user}
    ${RewardUnlockedStory.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RefereeExclusiveReward_currentUser>,
};
